import {ModelSetOptions} from 'backbone';
import _ from 'underscore';

import {isAntibodyComponentSchemaInterfaceId} from 'antibodies/schema-configuration/AntibodySchemaInterfaceConstants';
import {isBiologicsEnabled} from 'antibodies/util/biologicsCapability';
import BenchlingConfig from 'BenchlingConfig';
import {BufferViewLayout} from 'buffers/BufferComps';
import {doQuerySafe, readQuerySafe} from 'graphql-client/ApolloWrappers';
import AaSequence from 'molbio/aa-sequences/AaSequence';
import {removeLegacyAntibodyOptions} from 'molbio/antibody-engineering/visibilityHelpers';
import {LinearLayout} from 'molbio/maps/linear/LinearLayout';
import {AaSequenceLinearMapState} from 'molbio/maps/MapStates';
import SinglePolymerMapTabState, {
  SinglePolymerMapTabStateAttributes,
} from 'molbio/tabstates/SinglePolymerMapTabState';
import {TabStateDefaults} from 'Navigation';
import {SchemasWithInterfacesQuery} from 'schemas/schema-interface/__generated/SchemasWithInterfacesQuery';
import {SCHEMAS_WITH_INTERFACES_QUERY} from 'schemas/schema-interface/SchemaInterfaceConstants';
import {constructOptions, VisibilityOptions} from 'settings/visibility-options/VisibilityOptions';
import * as vo from 'settings/visibility-options/VisibilityOptions';
import * as logging from 'util/Logging';

export interface AaSequenceMapTabStateAttributes extends SinglePolymerMapTabStateAttributes<
  AaSequence,
  AaSequenceLinearMapState
> {
  visibilityOptions: VisibilityOptions;
  bufferViewLayout: BufferViewLayout | null;
  layout: LinearLayout;
}

interface AaSequenceMapTabState {
  get<K extends keyof AaSequenceMapTabStateAttributes>(key: K): AaSequenceMapTabStateAttributes[K];
  set<K extends keyof AaSequenceMapTabStateAttributes>(
    attributeName: K,
    value: AaSequenceMapTabStateAttributes[K],
    options?: ModelSetOptions
  ): AaSequenceMapTabState;
  set(obj: Partial<AaSequenceMapTabStateAttributes>): AaSequenceMapTabState;
}

class AaSequenceMapTabState extends SinglePolymerMapTabState<AaSequence, AaSequenceLinearMapState> {
  mapState(options: {polymer: AaSequence; visibilityOptions: VisibilityOptions}): AaSequenceLinearMapState {
    return new AaSequenceLinearMapState(options);
  }

  msVisibilityOptions(): VisibilityOptions {
    const editableOptions = this.get('visibilityOptions')
      .filter((opt) => opt.get('editable'))
      .map((opt) => opt.get('name'));
    return this.get('visibilityOptions').makeLocalSync({editableOptions});
  }

  /**
   * When both Biologics and the legacy antibody-tools flag are enabled, the legacy tools should
   * only appear on AA sequences that are NOT Biologics antibody-component entities (i.e. sequences
   * whose schema does not implement an antibody component SSI such as ssi_aadomain / ssi_aachain).
   * Those component entities are the AA chains and domains rendered on this map, so the check is
   * against the sequence's own tag schema, not against the top-level antibody schema.
   *
   * Schema-to-interface associations live in the SchemasWithInterfaces query. When it is
   * already cached we decide synchronously, before super.initialize() creates the map state, to
   * avoid any flicker. When it is not cached (e.g. a direct sequence load, where nothing has
   * fetched it yet) we keep the legacy tools visible and fetch the associations asynchronously,
   * removing the options only if the entity turns out to be an antibody-component schema. Keeping
   * the tools as the fallback ensures Mol Bio sequences are not incorrectly hidden while the query
   * is in flight.
   */
  override initialize(attributes?: Partial<AaSequenceMapTabStateAttributes>, options?: {}): void {
    if (isBiologicsEnabled() && BenchlingConfig.MOLBIO_ENABLE_ANTIBODY_ENGINEERING_TOOLS) {
      const protein = attributes?.protein ?? (attributes as {polymer?: AaSequence} | undefined)?.polymer;
      const tagSchemaId = protein?.tagSchemaId?.() ?? null;

      const cachedSchemaInterfaces = readQuerySafe<SchemasWithInterfacesQuery>({
        query: SCHEMAS_WITH_INTERFACES_QUERY,
      });

      if (cachedSchemaInterfaces != null) {
        if (this._isAntibodyComponentSchema(cachedSchemaInterfaces, tagSchemaId)) {
          this._removeLegacyAntibodyOptions();
        }
      } else if (tagSchemaId != null) {
        // Leave the legacy tools visible if the associations cannot be fetched, rather than
        // hiding tools a Mol Bio sequence may legitimately need.
        this._removeLegacyOptionsForAntibodyComponentAsync(tagSchemaId).catch((error) => {
          logging.exception(error, {level: logging.LEVEL_WARNING});
        });
      }
    }
    super.initialize(attributes, options);
  }

  override defaults(): TabStateDefaults & Partial<AaSequenceMapTabStateAttributes> {
    // Include legacy antibody annotation options whenever the config flag is on. For Biologics
    // tenants, initialize() removes them for antibody-component entities so they do not conflict
    // with Biologics-managed CDR annotations.
    const showLegacyAntibodyAnnotations = BenchlingConfig.MOLBIO_ENABLE_ANTIBODY_ENGINEERING_TOOLS;
    const visibilityOptions = _.compact([
      {base: vo.MAXIMISE, visible: true, editable: false, sync: false},
      {base: vo.AA_SEQUENCE_AXIS, visible: true, editable: false, sync: true},
      {base: vo.DNA, visible: true, editable: false, sync: true},
      {base: vo.ANNOTATIONS, visible: true, editable: true, sync: true},
      BenchlingConfig.MOLBIO_ENABLE_AA_PARTS
        ? {base: vo.PART_ANNOTATIONS, visible: true, editable: true, sync: true}
        : null,
      {base: vo.SEARCH_AMINO_ACIDS, visible: true, editable: false, sync: true},
      {base: vo.AA_SEQUENCE_COLORING, visible: true, editable: true, sync: true},
      {base: vo.PROTEIN_HYDROPHOBICITY_GRAPH, visible: false, editable: true, sync: true},
      {base: vo.PROTEIN_PI_GRAPH, visible: false, editable: true, sync: true},
      showLegacyAntibodyAnnotations
        ? {base: vo.ANTIBODY_NUMBERING, visible: false, editable: true, sync: true}
        : null,
      showLegacyAntibodyAnnotations
        ? {base: vo.ANTIBODY_CDR_ANNOTATIONS, visible: false, editable: true, sync: true}
        : null,
      showLegacyAntibodyAnnotations
        ? {base: vo.PROTEIN_LIABILITY_SITES, visible: false, editable: true, sync: true}
        : null,
      BenchlingConfig.MOLBIO_ENABLE_AA_PARTS ? {base: vo.PARTS, visible: true, editable: false} : null,
    ]);
    return {
      ...super.defaults(),
      closeable: false,
      title: 'Sequence Map',
      bufferViewLayout: null,
      visibilityOptions: constructOptions('linear', visibilityOptions),
      // Persisted: FileState.saveLayoutPreference writes this value into
      // `workspaceBuffers.${fileType}` via POST /1/api/users/me/preferences, and
      // loadLayout matches on it to restore a tab. It also feeds the URL `tab`
      // param through activateTabByIdentifier, so shared links carry it too.
      // Renaming orphans saved layouts and breaks those links.
      // This is not the map's DOM id, which AaSequenceMapView sets to
      // 'aa-sequence-map'. The two are independent strings that only look alike.
      preferenceKey: 'protein-map',
    };
  }

  private _isAntibodyComponentSchema(
    schemaInterfaces: SchemasWithInterfacesQuery,
    tagSchemaId: string | null
  ): boolean {
    if (tagSchemaId == null) {
      return false;
    }
    const tagSchema = schemaInterfaces.tagSchemas.find((schema) => schema.id === tagSchemaId);
    return (
      tagSchema != null &&
      tagSchema.interfaces.some((schemaInterface) => isAntibodyComponentSchemaInterfaceId(schemaInterface.id))
    );
  }

  /**
   * Fetches the schema-to-interface associations (populating the Apollo cache if needed) and, once
   * they resolve, removes the legacy options if this entity's schema is an antibody component. Used
   * when the associations are not yet cached at initialization time.
   */
  private async _removeLegacyOptionsForAntibodyComponentAsync(tagSchemaId: string): Promise<void> {
    const schemaInterfaces = await doQuerySafe<SchemasWithInterfacesQuery>({
      query: SCHEMAS_WITH_INTERFACES_QUERY,
    });
    if (this._isAntibodyComponentSchema(schemaInterfaces, tagSchemaId)) {
      this._removeLegacyAntibodyOptions();
    }
  }

  private _removeLegacyAntibodyOptions(): void {
    removeLegacyAntibodyOptions(this.get('visibilityOptions'));
    const mapState = this.get('mapstate');
    if (mapState != null) {
      removeLegacyAntibodyOptions(mapState.get('visibilityOptions'));
    }
  }
}

export default AaSequenceMapTabState;
